import type { User, SysInfo } from '@/types';

declare global {
  interface Window {
    __ENV?: {
      API_URL?: string;
    };
    // Dev-only request counter helper
    __reqCounter?: {
      total: number;
      byUrl: Map<string, number>;
      inc: (url: string) => void;
      print: () => void;
      reset: () => void;
    };
  }
}

// In development, prefer Vite env (.env.local) so local dev works even if public/config.js exists.
// In production, prefer runtime window.__ENV to allow changing API without rebuild on static hosts.
const isDev = (import.meta.env as unknown as { DEV?: boolean }).DEV === true;
const viteApiBase = (import.meta.env as unknown as { VITE_API_BASE_URL?: string }).VITE_API_BASE_URL;
const viteApiUrl = (import.meta.env as unknown as { VITE_API_URL?: string }).VITE_API_URL;
const runtimeApiUrl: string | undefined =
  typeof window !== 'undefined' && typeof window.__ENV?.API_URL === 'string'
    ? window.__ENV.API_URL
    : undefined;

const API_BASE_URL = isDev
  ? // DEV: use Vite env first, then fallback to relative '/api'
    viteApiBase || viteApiUrl || '/api'
  : // PROD: use runtime (config.js) first, then Vite env, then fallback
    runtimeApiUrl || viteApiBase || viteApiUrl || '/api';

let authToken: string | null = null;

// Optional global handler for 401 Unauthorized responses
let onUnauthorized: (() => void) | undefined;
export function setOnUnauthorized(handler: (() => void) | undefined) {
  onUnauthorized = handler;
}

export function setAuthToken(token: string | null) {
  authToken = token;
}

// Simple in-flight coalescing and short-lived cache (GET only)
type CacheEntry = { at: number; data: unknown };
// Coalesce GETs by parsed JSON promise, not by Response, to avoid cloning a consumed body
const inflightGets = new Map<string, Promise<unknown>>();
const recentGetCache = new Map<string, CacheEntry>();
const GET_CACHE_TTL_MS = 1200; // prevent dev StrictMode double-mount duplicates transparently

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    ...(options.headers as Record<string, string> | undefined),
  };

  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  const url = `${API_BASE_URL}${path}`;

  // Dev-only: initialize simple request counter once and count this request
  // Adds a simple counter to track the number of requests made to the API during development.
  // This is useful for monitoring the number of requests made to the API during development and
  // debugging purposes. The counter is only active in development mode and is not included in the
  // production build.
  // The counter is stored in the `window.__reqCounter` object, which has the following properties:
  // - `total`: The total number of requests made to the API.
  // - `byUrl`: A Map that stores the number of requests made to each URL.
  // - `inc(url: string)`: Increments the total count and the count for the specified URL.
  // - `print()`: Logs the total count and the count for each URL to the console.
  // - `reset()`: Resets the total count and clears the URL count.
  if (isDev) {
    
    if (!window.__reqCounter) {
      const byUrl = new Map<string, number>();
      window.__reqCounter = {
        total: 0,
        byUrl,
        inc(url: string) {
          this.total += 1;
          this.byUrl.set(url, (this.byUrl.get(url) || 0) + 1);
          console.debug(`[api] ${url} -> total=${this.total}, urlCount=${this.byUrl.get(url)}`);
        },
        print() {
          console.group(`[api] Total requests: ${this.total}`);
          for (const [u, c] of this.byUrl.entries()) {
            console.log(`${c} × ${u}`);
          }
          console.groupEnd();
        },
        reset() {
          this.total = 0;
          this.byUrl.clear();
          console.debug('[api] counters reset');
        },
      };
    }
    try {
      window.__reqCounter?.inc(url);
    } catch {
      // noop
    }
  }

  const method = String((options.method || 'GET')).toUpperCase();
  const isGet = method === 'GET' || (!options.method);
  const cacheKey = `${method} ${url}`;

  // Serve from short-lived cache or in-flight promise when present (GET only)
  if (isGet) {
    const cached = recentGetCache.get(cacheKey);
    if (cached && Date.now() - cached.at < GET_CACHE_TTL_MS) {
      return cached.data as T;
    }
    const running = inflightGets.get(cacheKey);
    if (running) {
      const data = await running;
      return data as T;
    }
    // Create a new in-flight promise that fully handles fetch, errors and JSON parsing
    const inflight = (async () => {
      const res = await fetch(url, { ...options, headers, credentials: 'include' });
      if (!res.ok) {
        // Unauthorized: only force logout if we actually have a token set.
        // This avoids logging out during boot when anonymous requests 401 as expected.
        if (res.status === 401 && authToken) {
          if (typeof onUnauthorized === 'function') {
            try { onUnauthorized(); } catch { /* noop */ }
          }
          throw new Error('Sessão expirada. Faça login novamente.');
        }
        let message = 'Erro ao processar a requisição';
        try {
          const data = await res.json();
          if (data && typeof data === 'object') {
            if (typeof (data as { message?: unknown }).message === 'string' && (data as { message: string }).message.trim() !== '') {
              message = (data as { message: string }).message;
            }
            if ((data as { errors?: Record<string, unknown> }).errors && typeof (data as { errors: Record<string, unknown> }).errors === 'object') {
              const parts: string[] = [];
              for (const key of Object.keys((data as { errors: Record<string, unknown> }).errors)) {
                const val = (data as { errors: Record<string, unknown> }).errors[key];
                if (Array.isArray(val)) {
                  parts.push(...(val as Array<unknown>).map((v) => String(v)));
                } else if (typeof val === 'string') {
                  parts.push(val);
                }
              }
              if (parts.length > 0) {
                message = parts.join('\n');
              }
            }
          }
        } catch (_) {
          // ignore JSON parse errors; keep default message
        }
        throw new Error(message);
      }
      if (res.status === 204) return undefined as unknown as T;
      const data = await res.json() as T;
      recentGetCache.set(cacheKey, { at: Date.now(), data });
      return data;
    })();
    inflightGets.set(cacheKey, inflight);
    try {
      const data = await inflight as T;
      return data;
    } finally {
      inflightGets.delete(cacheKey);
    }
  }

  // Non-GET: standard request path
  const res = await fetch(url, { ...options, headers, credentials: 'include' });
  if (!res.ok) {
    // Unauthorized: only force logout if we actually have a token set.
    // This avoids logging out during boot when anonymous requests 401 as expected.
    if (res.status === 401 && authToken) {
      if (typeof onUnauthorized === 'function') {
        try { onUnauthorized(); } catch { /* noop */ }
      }
      throw new Error('Sessão expirada. Faça login novamente.');
    }

    // Other errors: attempt to parse JSON and extract a useful message
    let message = 'Erro ao processar a requisição';
    try {
      const data = await res.json();
      if (data && typeof data === 'object') {
        if (typeof (data as { message?: unknown }).message === 'string' && (data as { message: string }).message.trim() !== '') {
          message = (data as { message: string }).message;
        }
        if ((data as { errors?: Record<string, unknown> }).errors && typeof (data as { errors: Record<string, unknown> }).errors === 'object') {
          const parts: string[] = [];
          for (const key of Object.keys((data as { errors: Record<string, unknown> }).errors)) {
            const val = (data as { errors: Record<string, unknown> }).errors[key];
            if (Array.isArray(val)) {
              parts.push(...(val as Array<unknown>).map((v) => String(v)));
            } else if (typeof val === 'string') {
              parts.push(val);
            }
          }
          if (parts.length > 0) {
            message = parts.join('\n');
          }
        }
      }
    } catch (_) {
      // ignore JSON parse errors; keep default message
    }
    throw new Error(message);
  }

  // No content
  if (res.status === 204) return undefined as unknown as T;

  const data = await res.json() as T;
  if (isGet) {
    recentGetCache.set(cacheKey, { at: Date.now(), data });
  }
  return data;
}

export const api = {
  postLogin: (email: string, password: string) =>
    request<{ user: User; token: string }>('/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  getUser: () => request<User>('/user'),
  postLogout: () => request<{ message: string }>('/logout', { method: 'POST' }),
  getSysInfo: () => request<SysInfo>('/sysinfo'),
  postForgotPassword: (email: string) =>
    request<{ message: string }>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  postResetPassword: (payload: { token: string; email: string; password: string; password_confirmation: string }) =>
    request<{ message: string }>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
};

// Generic HTTP helpers (authenticated)
export const http = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'POST',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'PUT',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'PATCH',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
